import { Canvas } from 'fabric/fabric-impl';
import { useCallback, useEffect, useRef } from 'react';
import { Subscription } from 'rxjs';

import { useDrawingShapeContext } from '../../drawingShapeContext';
import { useFabricCanvasContext } from '../fabricCanvas.context';
import { useDrawingOperations } from './useDrawingOperations';

import { CANVAS_OBJECT_TYPE_PREFIX, DEFAULT_ZOOM } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { isCanvasSafe } from '~/core/utils/canvasSafety';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { selectCurrentRoute } from '~/redux/slices/route';
import { RouteEnum } from '~/redux/slices/route/route.types';
import {
  selectSelectedDrawingObjectName,
  updateSelectedDrawingObjectName,
} from '~/redux/slices/shape';

export const useDrawFabricCanvas = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);
  const selectedColor = useAppSelector(selectSelectedColor);
  const needRedrawCanvas = useAppSelector(selectNeedRedrawCanvas);
  const currentRoute = useAppSelector(selectCurrentRoute);
  const selectedDrawingObjectName = useAppSelector(
    selectSelectedDrawingObjectName
  );

  // Get drawingShapesRef for always-fresh shapes data (avoids stale closure issues)
  const { drawingShapesRef } = useFabricCanvasContext();

  const {
    getters: {
      areThereShapesInTheDrawingArea,
      drawingShapes,
      activeDrawingObject,
    },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const {
    clearAndUpdateShapes,
    updateTextsAndArea,
    fillCanvasAndRender,
    safeRenderCanvas,
    finalizeDrawing,
  } = useDrawingOperations();

  // Use a ref to manage subscriptions lifecycle independently of the draw cycle.
  // This prevents subscriptions from being lost when the effect re-runs but
  // shouldDrawShapes() returns false (e.g., when needRedrawCanvas is false).
  const subscriptionsRef = useRef<(Subscription | undefined)[]>([]);

  const shouldDrawShapes = useCallback(
    () =>
      currentRoute === RouteEnum.DESIGN &&
      needRedrawCanvas &&
      areThereShapesInTheDrawingArea &&
      !!drawingShapes &&
      selectedColor?.overlayColor,
    [
      currentRoute,
      needRedrawCanvas,
      areThereShapesInTheDrawingArea,
      drawingShapes,
      selectedColor,
    ]
  );

  const validateCanvasSafety = useCallback(() => {
    if (!isCanvasSafe(canvas)) {
      console.warn('Canvas not safe for drawing, skipping draw operation');

      return false;
    }

    return true;
  }, [canvas]);

  const ensureCanvasZoom = useCallback(() => {
    try {
      const currentZoom = canvas.getZoom();

      if (!currentZoom || currentZoom === 0 || !isFinite(currentZoom)) {
        canvas.setZoom(DEFAULT_ZOOM);
      }
    } catch (error) {
      console.warn('Error checking/setting zoom:', error);

      return false;
    }

    return true;
  }, [canvas]);

  const performDrawing = useCallback(() => {
    try {
      // We need to handle two different timing scenarios:
      // 1. New shapes added: State has more shapes than ref (Context state ahead of ref sync)
      // 2. Appliance mutations: Ref has mutated objects, same length (ref has fresher data)
      //
      // Solution: Use whichever source has more shapes (handles new shape addition),
      // or use ref if lengths are equal (handles appliance mutations with same shape count)
      const currentShapes =
        drawingShapes.length > drawingShapesRef.current.length
          ? drawingShapes
          : drawingShapesRef.current;

      if (
        !clearAndUpdateShapes(canvas, currentShapes) ||
        !selectedColor?.overlayColor
      ) {
        return;
      }

      // Unsubscribe old subscriptions BEFORE creating new ones.
      // This is done here (not in cleanup) to ensure subscriptions persist
      // when the effect re-runs but shouldDrawShapes() returns false.
      subscriptionsRef.current.forEach((subscription) =>
        subscription?.unsubscribe()
      );

      const { renameSubscriptions } = updateTextsAndArea(
        selectedColor.overlayColor,
        currentShapes
      );

      // Store new subscriptions in the ref
      subscriptionsRef.current = renameSubscriptions ?? [];

      if (!fillCanvasAndRender(canvas, selectedColor, currentShapes)) {
        return;
      }

      if (activeDrawingObject?.object?.type === 'shape') {
        const activeDrawingShape = currentShapes.find(
          ({ id }) => id === activeDrawingObject?.object?.id
        );

        if (
          activeDrawingShape?.label &&
          activeDrawingShape?.label !== selectedDrawingObjectName
        ) {
          dispatch(updateSelectedDrawingObjectName(activeDrawingShape.label));
        }
      }

      safeRenderCanvas(canvas);
      canvas.clone((canvasClone: Canvas) => {
        drawingTool.removeObjectByName(
          canvasClone,
          CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
        );
      });
      // Commit the shapes we used for drawing (currentShapes already chose the best source).
      // This ensures we don't accidentally overwrite with stale/empty data.
      updateDrawingShapes([...currentShapes]);
      finalizeDrawing();
    } catch (error) {
      console.error('Error drawing shapes on canvas:', error);
    }
  }, [
    dispatch,
    canvas,
    selectedColor,
    drawingShapes,
    drawingShapesRef,
    clearAndUpdateShapes,
    updateTextsAndArea,
    fillCanvasAndRender,
    safeRenderCanvas,
    finalizeDrawing,
    activeDrawingObject?.object?.id,
    activeDrawingObject?.object?.type,
    selectedDrawingObjectName,
    updateDrawingShapes,
  ]);

  const drawShapes = useCallback(() => {
    if (!shouldDrawShapes()) {
      return;
    }

    if (!validateCanvasSafety()) {
      return;
    }

    if (!ensureCanvasZoom()) {
      return;
    }

    performDrawing();
  }, [
    shouldDrawShapes,
    validateCanvasSafety,
    ensureCanvasZoom,
    performDrawing,
  ]);

  // Effect to trigger drawing when dependencies change
  useEffect(() => {
    drawShapes();
  }, [drawShapes]);

  // Cleanup subscriptions only on unmount
  useEffect(
    () => () => {
      subscriptionsRef.current.forEach((subscription) =>
        subscription?.unsubscribe()
      );
    },
    []
  );

  return {
    drawShapes,
  };
};
